/**
 * My cards (redone 2026-10-17, as the owner asked): the account as a button in the corner,
 * the logo as the way to the front page (no "Home page" link), and no padlock note. The
 * cards show as big icons - a gallery that shows no one's photos, for a shared or work
 * computer - or as a list, for many cards; a click opens the card in the editor. "New card"
 * opens the card-making steps (new-card.tsx), and saving and opening card files sit side
 * by side above the cards (safekeeping.tsx).
 *
 * 2026-10-23, the owner: the events dashboard at the top, all the time (events-board.tsx);
 * "Create" asks first whether it is one card or an event (new-card.tsx); and the cards as "a
 * list that can scroll down, not something that occupies the whole page" - the big icons went,
 * and the list scrolls inside its own box.
 *
 * 2026-10-24, the owner: "+ New card" and "+ New event" side by side at the top - the card on the
 * left, the event on the right - and what they open comes up right under them.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { EventsBoard } from '../events-board.tsx';
import { api, ApiError, type ImageItem, type ProjectItem } from '../api.ts';
import type { LocalEvent } from '../local/events.ts';
import { askConfirm } from '../confirm.tsx';
import { download } from '../card-pdf.tsx';
import { saveCardFile } from '../local/card-file.ts';
import { onCardEvent } from '../local/store.ts';
import { PICTURE_ACCEPT } from '../local/pictures.ts';
import { Logo } from '../logo.tsx';
import { SaveOpenBar } from '../safekeeping.tsx';
import { Link, useLocation } from '../router.tsx';
import { ago, t } from '../strings.ts';
import { ThemeSwitch } from '../theme.tsx';
import { SignOutButton } from '../idle.tsx';
import { TipButton } from '../tip.tsx';
import { NewCard, type DesignChoice, type MakeKind } from './new-card.tsx';

/**
 * Add several pictures one after another (prepared and kept on this device),
 * reporting each refusal by name. They belong to the card they are added to.
 */
export function useUploader(onDone: (img: ImageItem) => void, projectId: string | null = null) {
  const [pending, setPending] = useState(0);
  const [errors, setErrors] = useState<string[]>([]);
  const upload = async (files: FileList | File[]) => {
    const list = [...files];
    setErrors([]);
    setPending(list.length);
    for (const f of list) {
      try {
        onDone(await api.upload(f, projectId));
      } catch (err) {
        setErrors((e) => [...e, `${f.name}: ${(err as Error).message}`]);
      } finally {
        setPending((n) => n - 1);
      }
    }
  };
  return { pending, errors, upload };
}

export function UploadButton({ uploader, className }: { uploader: ReturnType<typeof useUploader>; className?: string }) {
  const input = useRef<HTMLInputElement>(null);
  return (
    <>
      <input ref={input} type="file" accept={PICTURE_ACCEPT} multiple hidden
        onChange={(e) => { if (e.target.files?.length) uploader.upload(e.target.files); e.target.value = ''; }} />
      <button className={className} onClick={() => input.current?.click()} disabled={uploader.pending > 0}>
        {uploader.pending > 0 ? t('uploading', { n: uploader.pending }) : t('upload')}
      </button>
    </>
  );
}

/** A closed classic pocket card: portrait, its flap tucked in on a slant. */
function ClassicIcon() {
  return (
    <svg viewBox="0 0 40 48" className="design-icon" aria-hidden="true">
      <rect x="6" y="2" width="28" height="44" rx="1.5" className="di-paper" />
      <path d="M6 30 L34 8 L34 26 Z" className="di-flap" />
      <path d="M6 30 L34 8" className="di-line" />
    </svg>
  );
}
/** A closed letterfold: a square envelope, its point tucked into the pocket. */
function LetterfoldIcon() {
  return (
    <svg viewBox="0 0 48 48" className="design-icon" aria-hidden="true">
      <rect x="4" y="4" width="40" height="40" rx="1.5" className="di-paper" />
      <rect x="4" y="21" width="40" height="23" className="di-flap" />
      <path d="M4 21 H44" className="di-line" />
      <circle cx="24" cy="29" r="5" className="di-seal" />
    </svg>
  );
}
/** A closed puzzle purse: four panels woven round the middle, two of them lying on top. */
function PurseIcon() {
  return (
    <svg viewBox="0 0 40 48" className="design-icon" aria-hidden="true">
      <rect x="4" y="2" width="32" height="42" rx="1.5" className="di-paper" />
      <path d="M4 2 H20 V23 H4 Z" className="di-flap" />
      <path d="M20 23 H36 V44 H20 Z" className="di-flap" />
      <path d="M20 2 V44 M4 23 H36" className="di-line" />
    </svg>
  );
}
/** A closed fortune teller from above: a square of four flaps meeting in the middle. */
function TellerIcon() {
  return (
    <svg viewBox="0 0 40 48" className="design-icon" aria-hidden="true">
      <rect x="4" y="8" width="32" height="32" rx="1.5" className="di-paper" />
      <path d="M4 8 L20 24 L36 8 Z" className="di-flap" />
      <path d="M4 40 L20 24 L36 40 Z" className="di-flap" />
      <path d="M4 8 L36 40 M36 8 L4 40" className="di-line" />
    </svg>
  );
}
/** A closed surprise card: one picture made of four corners. */
function SurpriseIcon() {
  return (
    <svg viewBox="0 0 40 48" className="design-icon" aria-hidden="true">
      <rect x="4" y="2" width="32" height="42" rx="1.5" className="di-paper" />
      <circle cx="20" cy="21" r="9" className="di-flap" />
      <path d="M20 2 V44 M4 23 H36" className="di-line" strokeDasharray="2 2" />
    </svg>
  );
}
/** A closed twisted purse: a square, the four arms of its pinwheel turning round the middle. */
function TwistedIcon() {
  return (
    <svg viewBox="0 0 40 48" className="design-icon" aria-hidden="true">
      <rect x="4" y="8" width="32" height="32" rx="1.5" className="di-paper" />
      <path d="M20 24 L4 8 L20 8 Z M20 24 L36 8 L36 24 Z M20 24 L36 40 L20 40 Z M20 24 L4 40 L4 24 Z" className="di-flap" />
      <path d="M4 8 L36 40 M36 8 L4 40" className="di-line" />
    </svg>
  );
}
/** A loaded paper banger: a triangle, tip to the left, right angle down - and the bang. */
function BangerIcon() {
  return (
    <svg viewBox="0 0 40 48" className="design-icon" aria-hidden="true">
      <path d="M3 16 H37 L20 33 Z" className="di-paper" />
      <path d="M37 16 L20 33 L28 41 L37 32 Z" className="di-flap" />
      <path d="M8 16 V21 M3 16 L8 21" className="di-line" />
      <path d="M31 4 L32.5 9 L37 7 L34.5 11 M24 6 L26 10" className="di-line" />
    </svg>
  );
}
/** A closed hexagonal packet: a long six-sided card, a point at each end. */
function PacketIcon() {
  return (
    <svg viewBox="0 0 40 48" className="design-icon" aria-hidden="true">
      <path d="M20 2 L33 14 V34 L20 46 L7 34 V14 Z" className="di-paper" />
      <path d="M20 2 L33 14 H7 Z M20 46 L33 34 H7 Z" className="di-flap" />
      <path d="M31 14 V34" className="di-line" />
    </svg>
  );
}
/** A closed square letter: a square on its point, the two bands down its middle. */
function SquareIcon() {
  return (
    <svg viewBox="0 0 40 48" className="design-icon" aria-hidden="true">
      <path d="M20 6 L38 24 L20 42 L2 24 Z" className="di-paper" />
      <path d="M16 10 L20 6 L24 10 V38 L20 42 L16 38 Z" className="di-flap" />
      <path d="M20 6 V42" className="di-line" />
    </svg>
  );
}
/** A photo frame: a band all round the window, a mitre at each corner. */
function FrameIcon() {
  return (
    <svg viewBox="0 0 40 48" className="design-icon" aria-hidden="true">
      <path d="M5 4 H35 V44 H5 Z M12 11 V37 H28 V11 Z" className="di-paper" fillRule="evenodd" />
      <path d="M12 11 H28 V37 H12 Z" className="di-flap" />
      <path d="M5 4 L12 11 M35 4 L28 11 M35 44 L28 37 M5 44 L12 37" className="di-line" />
    </svg>
  );
}
export const DESIGNS: DesignChoice[] = [
  { id: 'classic', name: 'designClassic', about: 'designClassicAbout', Icon: ClassicIcon, story: 'story_classic' },
  // a trial (2026-10-11): the classic card with the two half circles of Reveal 1 lined up; archived 2026-10-18
  // (the owner: keep one of the two) - the classic slides its halves and lines them up too
  { id: 'classic-aligned', name: 'designAligned', about: 'designAlignedAbout', Icon: ClassicIcon, archived: true },
  // archived 2026-10-18 (the owner: "remove the letter fold mode, it is too basic")
  { id: 'letterfold', name: 'designLetterfold', about: 'designLetterfoldAbout', Icon: LetterfoldIcon, archived: true },
  // a trial (2026-10-17): the traditional puzzle purse, not yet folded on paper
  { id: 'puzzle-purse', name: 'designPurse', about: 'designPurseAbout', Icon: PurseIcon, story: 'story_purse' },
  // a trial (2026-10-17): the playground fortune teller, not yet folded on paper
  { id: 'fortune-teller', name: 'designTeller', about: 'designTellerAbout', Icon: TellerIcon, story: 'story_teller' },
  // a trial (2026-10-17): the traditional hidden-picture fold, not yet folded on paper
  // archived 2026-10-20 (the owner, after folding it: "it doesn't seem the sides stick")
  { id: 'surprise-card', name: 'designSurprise', about: 'designSurpriseAbout', Icon: SurpriseIcon, archived: true },
  // a trial (2026-10-17): the traditional twisted purse, one twist, not yet folded on paper
  { id: 'twisted-purse', name: 'designTwisted', about: 'designTwistedAbout', Icon: TwistedIcon, story: 'story_twisted' },
  // a trial (2026-10-25): the traditional paper banger, a party popper, not yet folded on paper
  { id: 'paper-banger', name: 'designBanger', about: 'designBangerAbout', Icon: BangerIcon, story: 'story_banger' },
  // a trial (2026-10-25): the traditional hexagonal seed packet, not yet folded on paper
  { id: 'hexagonal-packet', name: 'designPacket', about: 'designPacketAbout', Icon: PacketIcon, story: 'story_packet' },
  // a trial (2026-10-31): the traditional square letterfold (1694), not yet folded on paper
  { id: 'square-letter', name: 'designSquare', about: 'designSquareAbout', Icon: SquareIcon, story: 'story_square' },
  // a trial (2026-10-31): the traditional decorative frame ('Cadre', Paris 1894), not yet folded on paper
  { id: 'photo-frame', name: 'designFrame', about: 'designFrameAbout', Icon: FrameIcon, story: 'story_frame' },
];

/** Soft colours for the card icons, one per card, always the same for that card. */
const TINTS = ['#f3b6c4', '#a8d5e2', '#f7d58b', '#b9dcb0', '#c9b8ec', '#f4bf9a', '#9fc4ef', '#e5c6a0'];
const tintOf = (id: string) => TINTS[[...id].reduce((h, c) => (h * 31 + c.charCodeAt(0)) >>> 0, 7) % TINTS.length];
const initialOf = (title: string) => [...title.trim()][0]?.toUpperCase() ?? '?';

/**
 * A card as a small icon: the design's shape in the card's own colour and the first letter
 * of its name - never its photos, which stay private on a shared screen.
 */
function CardArt({ card, small = false }: { card: ProjectItem; small?: boolean }) {
  const letterfold = card.templateId === 'letterfold';
  const purse = card.templateId === 'puzzle-purse';
  const teller = card.templateId === 'fortune-teller';
  const surprise = card.templateId === 'surprise-card';
  const twisted = card.templateId === 'twisted-purse';
  const banger = card.templateId === 'paper-banger';
  const packet = card.templateId === 'hexagonal-packet';
  const square = card.templateId === 'square-letter';
  const frame = card.templateId === 'photo-frame';
  const sealY = letterfold ? 60 : purse || surprise || packet ? 47 : teller || twisted || square || frame ? 48 : banger ? 42 : 72;
  return (
    <span className={`card-art${small ? ' small' : ''}`} style={{ '--tint': tintOf(card.id) } as React.CSSProperties} aria-hidden="true">
      <svg viewBox="0 0 100 100">
        {letterfold ? (
          <>
            <rect x="12" y="16" width="76" height="70" rx="5" className="ca-paper" />
            <path d="M12 46 L50 70 L88 46" className="ca-fold" />
            <path d="M12 46 H88 V81 a5 5 0 0 1 -5 5 H17 a5 5 0 0 1 -5 -5 Z" className="ca-flap" />
          </>
        ) : teller ? (
          <>
            <rect x="16" y="14" width="68" height="68" rx="5" className="ca-paper" />
            <path d="M18 16 L50 48 L82 16 Z" className="ca-flap" />
            <path d="M18 80 L50 48 L82 80 Z" className="ca-flap" />
            <path d="M16 14 L84 82 M84 14 L16 82" className="ca-fold" />
          </>
        ) : frame ? (
          <>
            <path d="M18 6 H82 V90 H18 Z M32 20 V76 H68 V20 Z" className="ca-paper" fillRule="evenodd" />
            <rect x="32" y="20" width="36" height="56" className="ca-flap" />
            <path d="M18 6 L32 20 M82 6 L68 20 M82 90 L68 76 M18 90 L32 76" className="ca-fold" />
          </>
        ) : square ? (
          <>
            <path d="M50 8 L90 48 L50 88 L10 48 Z" className="ca-paper" />
            <path d="M40 18 L50 8 L60 18 V78 L50 88 L40 78 Z" className="ca-flap" />
            <path d="M50 8 V88" className="ca-fold" />
          </>
        ) : packet ? (
          <>
            <path d="M50 6 L80 30 V66 L50 90 L20 66 V30 Z" className="ca-paper" />
            <path d="M50 6 L80 30 H20 Z M50 90 L80 66 H20 Z" className="ca-flap" />
            <path d="M76 30 V66" className="ca-fold" />
          </>
        ) : banger ? (
          <>
            <path d="M10 28 H90 L50 68 Z" className="ca-paper" />
            <path d="M90 28 L50 68 L68 86 L90 64 Z" className="ca-flap" />
            <path d="M50 68 L90 28" className="ca-fold" />
          </>
        ) : twisted ? (
          <>
            <rect x="16" y="14" width="68" height="68" rx="5" className="ca-paper" />
            <path d="M50 48 L18 16 L50 14 Z M50 48 L82 16 L84 48 Z M50 48 L82 80 L50 82 Z M50 48 L18 80 L16 48 Z" className="ca-flap" />
            <path d="M16 14 L84 82 M84 14 L16 82" className="ca-fold" />
          </>
        ) : surprise ? (
          <>
            <rect x="22" y="10" width="56" height="74" rx="5" className="ca-paper" />
            <circle cx="50" cy="47" r="22" className="ca-flap" />
            <path d="M50 10 V84 M22 47 H78" className="ca-fold" strokeDasharray="3 3" />
          </>
        ) : purse ? (
          <>
            <rect x="22" y="10" width="56" height="74" rx="5" className="ca-paper" />
            <path d="M27 10 H50 V47 H22 V15 a5 5 0 0 1 5 -5 Z" className="ca-flap" />
            <path d="M50 47 H78 V79 a5 5 0 0 1 -5 5 H50 Z" className="ca-flap" />
            <path d="M50 10 V84 M22 47 H78" className="ca-fold" />
          </>
        ) : (
          <>
            <rect x="24" y="8" width="52" height="84" rx="5" className="ca-paper" />
            <path d="M24 62 L76 22 L76 54 Z" className="ca-flap" />
            <path d="M24 62 L76 22" className="ca-fold" />
          </>
        )}
        <circle cx="50" cy={sealY} r={small ? 15 : 13} className="ca-seal" />
        <text x="50" y={sealY} className="ca-letter" textAnchor="middle" dominantBaseline="central">{initialOf(card.title)}</text>
      </svg>
    </span>
  );
}

/** The "⋯" menu of a card: save it to a file, delete it. */
function CardMenu({ card, onDelete, onSaved }: { card: ProjectItem; onDelete: () => void; onSaved: (msg: string) => void }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  /** Where the menu opens: fixed to the window, so the list's scrolling box never cuts it off. */
  const [at, setAt] = useState<React.CSSProperties>({});
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const away = (e: Event) => { if (!box.current?.contains(e.target as Node)) setOpen(false); };
    const key = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    // the menu stays where it opened: scrolling the list or the page closes it instead
    const scrolled = (e: Event) => { if (!box.current?.contains(e.target as Node)) setOpen(false); };
    addEventListener('pointerdown', away);
    addEventListener('keydown', key);
    addEventListener('scroll', scrolled, true);
    return () => { removeEventListener('pointerdown', away); removeEventListener('keydown', key); removeEventListener('scroll', scrolled, true); };
  }, [open]);
  const toggle = (button: HTMLElement) => {
    if (!open) {
      const r = button.getBoundingClientRect();
      // below the button, or above it when there is no room below
      setAt(r.bottom + 110 > innerHeight
        ? { position: 'fixed', bottom: innerHeight - r.top + 4, right: innerWidth - r.right, top: 'auto' }
        : { position: 'fixed', top: r.bottom + 4, right: innerWidth - r.right });
    }
    setOpen(!open);
  };
  const save = async () => {
    setBusy(true);
    try {
      const { blob, name } = await saveCardFile(card.id);
      download(blob, name);
      onSaved(t('cardSaved'));
    } finally {
      setBusy(false);
      setOpen(false);
    }
  };
  return (
    <div className="card-menu" ref={box}>
      <button className="icon menu-btn" aria-label={t('cardMenu', { title: card.title })} title={t('cardMenu', { title: card.title })}
        aria-expanded={open} onClick={(e) => toggle(e.currentTarget)}>⋯</button>
      {open && (
        <div className="menu" role="menu" style={at}>
          <button role="menuitem" onClick={() => void save()} disabled={busy}>{busy ? t('savingCard') : t('saveCardShort')}</button>
          <button role="menuitem" className="danger-text" onClick={() => { setOpen(false); onDelete(); }}>{t('deleteCard')}</button>
        </div>
      )}
    </div>
  );
}

export function HomePage({ email, name = '', approver = false }: { email: string; name?: string; approver?: boolean }) {
  const verified = useLocation().searchParams.get('verified');
  const [projects, setProjects] = useState<ProjectItem[] | null>(null);
  const [images, setImages] = useState<ImageItem[] | null>(null);
  const [designs, setDesigns] = useState<string[]>(['classic']);
  const [notice, setNotice] = useState<string | null>(null);
  const [good, setGood] = useState<string | null>(null);
  /** Making something new: what was already chosen (an event, or another card for one, from the events board), or null to ask. */
  const [making, setMaking] = useState<{ start: MakeKind | null; forEvent?: LocalEvent; n: number } | null>(null);
  const [query, setQuery] = useState('');
  const maker = useRef<HTMLDivElement>(null);
  const make = (start: MakeKind | null, forEvent?: LocalEvent) => {
    setMaking((m) => ({ start, forEvent, n: (m?.n ?? 0) + 1 }));
    setTimeout(() => maker.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  };
  /** Pictures in no card - added from this page before each card had its own (local/store.ts adoptPictures). */
  const leftovers = (images || []).filter((i) => !i.projectId || !(projects || []).some((p) => p.id === i.projectId));

  const load = () => {
    api.projects().then((r) => setProjects(r.projects));
    api.images().then((r) => setImages(r.images));
  };
  // cards also arrive from the card folder (local/folder.ts) and from opened files
  useEffect(() => {
    let timer: number | undefined;
    const off = onCardEvent(() => { window.clearTimeout(timer); timer = window.setTimeout(load, 300); });
    return () => { off(); window.clearTimeout(timer); };
  }, []);
  useEffect(() => {
    load();
    // only the designs this server has published
    api.templates().then((r) => setDesigns(DESIGNS.map((d) => d.id).filter((id) => r.templates.some((x) => x.id === id))))
      .catch(() => {});
  }, []);

  const removeImage = async (img: ImageItem) => {
    try {
      await api.deleteImage(img.id);
      setImages((l) => (l || []).filter((x) => x.id !== img.id));
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setNotice(t('pictureInUse', { titles: err.body.projects.map((p: { title: string }) => p.title).join(', ') }));
      } else throw err;
    }
  };

  const removeProject = async (p: ProjectItem) => {
    if (!(await askConfirm({ title: t('confirmDeleteCard', { title: p.title }), yes: t('deleteCard'), danger: true }))) return;
    await api.deleteProject(p.id);
    setProjects((l) => (l || []).filter((x) => x.id !== p.id));
  };

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (projects || []).filter((p) => !q || p.title.toLowerCase().includes(q));
  }, [projects, query]);
  const designName = (id: string) => t(DESIGNS.find((d) => d.id === id)?.name ?? 'designClassic');
  const none = projects !== null && projects.length === 0;
  const who = name.trim() || email;

  return (
    <main className="page cards-page">
      <header className="topbar">
        <Link href="/welcome" className="brand" title={t('footerHome')}><Logo /></Link>
        <div className="right">
          {approver && <Link href="/admin" className="button small">{t('admin')}</Link>}
          <ThemeSwitch compact />
          <Link href="/account" className="button account-btn" title={t('accountButton', { name: who })}
            aria-label={t('accountButton', { name: who })}>
            <span className="avatar" aria-hidden="true">{initialOf(who)}</span>
            <span className="account-name">{who}</span>
          </Link>
          <TipButton />
          <SignOutButton />
        </div>
      </header>
      {verified && <p className="ok banner">{t('verifiedWelcome')}</p>}
      {notice && <p className="error banner" role="alert" onClick={() => setNotice(null)}>{notice}</p>}
      {good && <p className="ok banner" role="status" onClick={() => setGood(null)}>{good}</p>}

      {/* making something: a card on the left, an event on the right, and the steps right under them */}
      <div className="make-row" ref={maker}>
        <button className="primary big-btn" onClick={() => make('card')}>
          <svg className="btn-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M12 5v14M5 12h14" /></svg>{t('newCard')}
        </button>
        <button className="primary big-btn" onClick={() => make('event')}>
          <svg className="btn-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M12 5v14M5 12h14" /></svg>{t('eventsNew')}
        </button>
      </div>
      {(making || none) && projects !== null && (
        <NewCard key={making?.n ?? 0} start={making?.start ?? null} forEvent={making?.forEvent ?? null}
          designs={DESIGNS.filter((d) => !d.archived && designs.includes(d.id))} projects={projects} images={images || []}
          onCancel={none ? undefined : () => setMaking(null)} />
      )}

      <EventsBoard projects={projects} onAddCard={(e) => make('event', e)} />

      <div className="cards-head">
        <h1>{t('myCards')}{projects && projects.length > 0 && (
          <span className="cards-count">{projects.length === 1 ? t('cardsCountOne') : t('cardsCount', { n: projects.length })}</span>
        )}</h1>
      </div>

      <SaveOpenBar onCardsChanged={load} />

      {projects === null ? <p className="muted">{t('loading')}</p> : projects.length > 0 && (
        <section className="cards-section">
          {projects.length > 6 && (
            <div className="cards-tools">
              <input type="search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t('findCard')}
                aria-label={t('findCard')} className="card-search" />
            </div>
          )}
          {shown.length === 0 ? <p className="muted">{t('noCardsFound', { q: query })}</p> : (
            <ul className="card-list card-scroll">
              {shown.map((p) => (
                <li key={p.id}>
                  <Link href={`/projects/${p.id}`} className="list-open">
                    <CardArt card={p} small />
                    <strong className="list-title">{p.title}</strong>
                    <span className="muted small list-design">{designName(p.templateId)}</span>
                    <span className="muted small list-versions">{p.versions === 1 ? t('oneVersion') : t('versions', { n: p.versions ?? 1 })}</span>
                    <span className="muted small list-when">{t('edited', { when: ago(p.updatedAt) })}</span>
                  </Link>
                  <CardMenu card={p} onDelete={() => void removeProject(p)} onSaved={setGood} />
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {projects !== null && leftovers.length > 0 && (
        <section>
          <h2>{t('leftoverPictures')}</h2>
          <p className="muted small">{t('leftoverPicturesHint')}</p>
          <ul className="thumbs">
            {leftovers.map((img) => (
              <li key={img.id}>
                <img src={img.urls.thumb} alt={img.name || ''} loading="lazy" />
                <button className="icon" title={t('deletePicture')} aria-label={t('deletePicture')} onClick={() => removeImage(img)}>×</button>
              </li>
            ))}
          </ul>
        </section>
      )}
    </main>
  );
}
